/** Presentation helpers shared by the admin module and the partner portal. */

const CURRENCY_SYMBOLS: Record<string, string> = {
  NGN: '₦',
  USD: '$',
  GBP: '£',
  EUR: '€',
};

/** ₦5,000,000 — no decimals, because these are whole-naira project values. */
export const formatMoney = (
  amount: number | null | undefined,
  currency = 'NGN'
): string => {
  if (amount === null || amount === undefined || Number.isNaN(amount)) return '—';
  const symbol = CURRENCY_SYMBOLS[currency] || `${currency} `;
  return `${symbol}${Math.round(amount).toLocaleString()}`;
};

/** 0.1 → "10%" */
export const formatRate = (rate: number | null | undefined): string =>
  rate === null || rate === undefined || Number.isNaN(rate)
    ? '—'
    : `${(rate * 100).toFixed(rate * 100 % 1 === 0 ? 0 : 1)}%`;

export const formatDate = (value?: string | Date | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
};

export const formatDateTime = (value?: string | Date | null): string => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** "in 2 days" / "3 days ago" — how a follow-up date is actually read. */
export const relativeDays = (value?: string | Date | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const startOfDay = (d: Date) =>
    new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();

  const days = Math.round(
    (startOfDay(date) - startOfDay(new Date())) / 86_400_000
  );

  if (days === 0) return 'today';
  if (days === 1) return 'tomorrow';
  if (days === -1) return 'yesterday';
  return days > 0 ? `in ${days} days` : `${Math.abs(days)} days overdue`;
};

/** True when a scheduled follow-up has slipped past today. */
export const isOverdue = (value?: string | Date | null): boolean => {
  if (!value) return false;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date.getTime() < today.getTime();
};

/** `<input type="date">` wants yyyy-mm-dd in local time, not an ISO instant. */
export const toDateInput = (value?: string | Date | null): string => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

/** The browser's offset, sent so the server buckets follow-ups by the same day. */
export const timezoneOffset = (): number => new Date().getTimezoneOffset();
